import React, { useState, useEffect, useCallback } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import DataTable from "../component/DataTable";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SearchIcon from "@mui/icons-material/Search";
import NotificationModal from "../component/NotificationModal";
import { InputAdornment, MenuItem } from "@mui/material";
import Tooltip from '@mui/material/Tooltip';

const Customer = () => {

  const [rowsData, setRowsData] = useState([]);
  const [field, setField] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [loading, setLoading] = useState(false);

  // Notification Pop-up
  const [openNotify, setOpenNotify] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isBulk, setIsBulk] = useState(false);

  const defaultButtons = [
    { id: "btn_1", label: "", link: "", discountCode: "" },
    { id: "btn_2", label: "", link: "", discountCode: "" },
  ];

  const [notifyForm, setNotifyForm] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    buttons: defaultButtons.map((b) => ({
      id: b.id,
      label: "",
      link: "",
      discountCode: "",
    })),
    country: "",
  });

  const [notifyErrors, setNotifyErrors] = useState({
    title: "",
    body: "",
    image_url: "",
    link: "",
    buttons: "",
    country: "",
  });

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const getCountryCodes = async () => {
    try {
      const res = await axiosInstance.get("customer/getCountryCode");

      if (res.data.isSuccess) {
        setCountryList(res.data.data);
      }
    } catch (error) {
      console.error("Country API Error", error);
    }
  };

  const openNotificationModal = (row) => {
    setSelectedCustomer(row);
    setIsBulk(false);
    setOpenNotify(true);
  };

  const closeNotificationModal = () => {
    setOpenNotify(false);
    setNotifyForm({
      title: "",
      body: "",
      image_url: "",
      link: "",
      buttons: defaultButtons.map((b) => ({
        id: b.id,
        label: "",
        link: "",
        discountCode: "",
      })),
      country: "",
    });

    setNotifyErrors({
      title: "",
      body: "",
      image_url: "",
      link: "",
      buttons: "",
      country: "",
    });
  };

  const handleSendNotification = async () => {
    let errors = {};

    if (!notifyForm.title.trim()) {
      errors.title = "Title is required";
    }

    if (!notifyForm.body.trim()) {
      errors.body = "Body is required";
    }

    if (
      notifyForm.image_url.trim() &&
      !/^https?:\/\/.+/i.test(notifyForm.image_url)
    ) {
      errors.image_url = "Enter a valid image URL";
    }

    const invalidButtonLink = notifyForm.buttons.find((b) => {
      if (b.id !== "btn_1") return false;
      const link = b.link?.trim();
      return link && !/^https?:\/\/.+/i.test(link);
    });
    if (invalidButtonLink) errors.buttons = "Enter a valid URL for Button 1 link";

    setNotifyErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      // Buttons: fixed id (btn_1, btn_2)
      const buttons = notifyForm.buttons.map((b) => {
        const id = b.id;
        const label = b.label?.trim() || "";
        if (id === "btn_2") {
          return {
            id,
            label,
            discountCode: b.discountCode?.trim() || "",
          };
        }
        return {
          id,
          label,
          link: b.link?.trim() || "",
        };
      });

      // Common payload fields
      const basePayload = {
        title: notifyForm.title,
        body: notifyForm.body,
        imageUrl: notifyForm.image_url || null,
        link: notifyForm.link || null,
        buttons,
      };

      let response;

      if (isBulk) {
        // BULK NOTIFICATION (optionally filtered by country)
        response = await axiosInstance.post(
          "customer/sendPushNotification",
          {
            ...basePayload,
            country: notifyForm.country || null,
          }
        );
      } else {
        //  SINGLE NOTIFICATION
        if (!selectedCustomer?.fcmToken) {
          Swal.fire("Error!", "FCM token not found for this user", "error");
          return;
        }

        response = await axiosInstance.post(
          "customer/testPushNotification",
          {
            ...basePayload,
            fcmToken: selectedCustomer.fcmToken,
          }
        );
      }

      const { isSuccess, message } = response.data;

      if (isSuccess) {
        Swal.fire("Success!", message, "success");
        closeNotificationModal();
        fetchData();
      } else {
        Swal.fire("Error!", message || "Notification failed", "error");
      }

    } catch (error) {
      Swal.fire(
        "Error!",
        error?.response?.data?.message || "Something went wrong",
        "error"
      );
    }
  };

  const baseColumns = [
    {
      field: "customerEmail",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => {
        const value = params?.value;

        return (
          <div style={{ textAlign: value == null || undefined || "" ? "center" : "left", width: "100%" }}>
            {value == null || undefined || "" ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "customerId",
      headerName: "Customer Id",
      flex: 1,
      renderCell: (params) => {
        const value = params?.value;
        return (
          <div style={{ textAlign: value == null || undefined || "" ? "center" : "left", width: "100%" }}>
            {value == null || undefined || "" ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "deviceId",
      headerName: "Device Id",
      flex: 1,
      renderCell: (params) => {
        const value = params?.value;

        return (
          <div style={{ textAlign: value == null || undefined ? "center" : "left", width: "100%" }}>
            {value == null || undefined ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "countryCode",
      headerName: "Country",
      flex: 0.7,
      renderCell: (params) => {
        const value = params?.value;

        return (
          <div style={{ textAlign: value == null || undefined ? "center" : "left", width: "100%" }}>
            {value == null || undefined ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "platform",
      headerName: "Platform",
      flex: 0.7,
      renderCell: (params) => {
        const value = params?.value;

        return (
          <div style={{ textAlign: value == null || undefined ? "center" : "left", width: "100%" }}>
            {value == null || undefined ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "fcmToken",
      headerName: "FCM Token",
      flex: 1,
      renderCell: (params) => {
        const value = params?.value;

        return (
          <div style={{ textAlign: value == null || undefined ? "center" : "left", width: "100%" }}>
            {value == null || undefined ? "-" : value}
          </div>
        );
      }
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }).format(date);
      },
    },
    {
      field: "push",
      headerName: "Push",
      flex: 0.7,
      sortable: false,
      align: "center",
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            <Tooltip title="Push Notification">
              <NotificationsActiveIcon fontSize="small" />
            </Tooltip>}
          onClick={() => openNotificationModal(params.row)}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            fontSize: "0.75rem",
            padding: "4px 12px",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          }}
        >
          Push
        </Button>
      ),
    }
  ];

  const [columns, setColumns] = useState(baseColumns);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `customer/getCustomers?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`;

      // if search OR country filter exists → use search API
      if (debouncedSearch || (selectedCountry && selectedCountry !== "all")) {

        url = `customer/getCustomerBySearch?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`;

        if (debouncedSearch) {
          url += `&search=${debouncedSearch}`;
        }

        if (selectedCountry && selectedCountry !== "all") {
          url += `&country=${selectedCountry}`;
        }
      }

      const { data } = await axiosInstance.get(url);

      if (data?.data) {
        setRowsData(data.data);
        setTotalRecords(debouncedSearch || (selectedCountry && selectedCountry !== "all") ? data.total : data.totalRecords);
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, selectedCountry]);

  useEffect(() => {
    fetchData();
    getCountryCodes()
  }, [fetchData], []);

  useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      page: 0
    }));
  }, [debouncedSearch]);

  const keys = rowsData.length > 0 ? Object.keys(rowsData[0]) : [];

  const handleAddColumn = (e) => {
    e.preventDefault();

    if (!field || !headerName) return;

    const isDuplicate = columns.some((col) => col.field === field);
    if (isDuplicate) {
      Swal.fire("Field already exists!", "", "warning");
      return;
    }

    const newColumn = {
      field: field.trim(),
      headerName: headerName.trim(),
      flex: 1,
    };

    const actionCol = columns.find((col) => col.field === "actions");
    const otherCols = columns.filter((col) => col.field !== "actions");

    setColumns(
      actionCol
        ? [...otherCols, newColumn, actionCol]
        : [...otherCols, newColumn]
    );

    setField("");
    setHeaderName("");
  };

  return (
    <div className="content-wrapper pd-top">
      <div className="page-header mt-5">
        <h3 className="page-title">Customers</h3>
      </div>

      {/* Add Dynamic Column + Bulk */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <div style={{ width: "100%" }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography component="span">
                <h5 className="m-0">Add Dynamic Column</h5>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <form onSubmit={handleAddColumn}>
                <div className="row">
                  <div className="col-md-6">
                    <label>Field</label>
                    <select
                      className="form-control mb-3"
                      value={field}
                      onChange={(e) => setField(e.target.value)}
                    >
                      <option value="">Select field</option>
                      {keys.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label>Header Name</label>
                    <input
                      className="form-control mb-3"
                      placeholder="Header Name"
                      value={headerName}
                      onChange={(e) => setHeaderName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="text-end">
                  <button className="btn btn-primary">Add Column</button>
                </div>
              </form>
            </AccordionDetails>
          </Accordion>
        </div>

      </div>

      {/* Search Bar */}
      <div className="mb-4 d-flex"
      >
        <div style={{ flex: 1 }}>

          <TextField
            fullWidth
            style={{ width: "95%" }}
            size="small"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#d0d5dd",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#1976d2"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                  borderWidth: "1px",
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#1976d2" }} />
                </InputAdornment>
              )
            }}
          />
        </div>

        {/* COUNTRY FILTER */}
        <div>
          <TextField
            select
            size="small"
            label="Country"
            value={selectedCountry}
            SelectProps={{
              renderValue: (v) =>
                v === "" ? "" : v === "all" ? "All Countries" : v,
              displayEmpty: true,
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxHeight: 48 * 6,
                  },
                },
              },
            }}
            onChange={(e) => setSelectedCountry(e.target.value)}
            sx={{
              minWidth: 180,
              backgroundColor: "#fff",
              borderRadius: "8px",
            }}
          >

            <MenuItem value="all">
              All Countries
            </MenuItem>

            {countryList.map((item, index) => (
              <MenuItem key={index} value={item.countryCode}>
                {item.countryCode}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      {/* Table */}
      <DataTable
        rows={rowsData}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowCount={totalRecords}
        paginationMode="server"
        loading={loading}
      />

      {/* Notification Modal */}
      <NotificationModal
        open={openNotify}
        onClose={closeNotificationModal}
        isBulk={isBulk}
        notifyForm={notifyForm}
        setNotifyForm={setNotifyForm}
        notifyErrors={notifyErrors}
        setNotifyErrors={setNotifyErrors}
        onSend={handleSendNotification}
        countryList={countryList}
      />

    </div>
  );
};

export default Customer;
